import { useEffect, useMemo } from 'react';
import useTableFilter from '@/hooks/useTableFilter';
import {
  getWebhookActivities,
  getWebhookActivitiesSource,
  WEBHOOK_DATA_SOURCES,
} from '@/services/webhooks';
import { useQuery } from '@tanstack/react-query';
import {
  getWebhookEventOptions,
  isWebhookActivityInPeriod,
  mapWebhookActivity,
} from '@/utils/webhooks';

export const WEBHOOK_ACTIVITY_DEFAULT_FILTER = {
  status: [],
  period: [],
  events: [],
};
const WEBHOOK_ACTIVITY_PAGE_SIZE = 10;
const WEBHOOK_PREVIEW_LIMIT = WEBHOOK_ACTIVITY_PAGE_SIZE;
const WEBHOOK_ACTIVITY_PAGINATION_PLACEHOLDER = Object.freeze({
  count: 0,
  next: null,
  previous: null,
  results: [],
});
export const WEBHOOK_ACTIVITY_SCOPES = {
  PREVIEW: 'preview',
  FULL: 'full',
};

export default function useWebhookActivities({
  webhooks = [],
  scope = WEBHOOK_ACTIVITY_SCOPES.FULL,
} = {}) {
  const table = useTableFilter({
    defaultCurrentPage: 1,
    defaultRowsPerPage: WEBHOOK_ACTIVITY_PAGE_SIZE,
    defaultFilter: WEBHOOK_ACTIVITY_DEFAULT_FILTER,
  });
  const { filter, page, rowsPerPage, setPage, setRowsPerPage } = table;
  const currentPage = Math.max(Number(page) || 1, 1);
  const usesPagination = scope === WEBHOOK_ACTIVITY_SCOPES.FULL;
  const shouldFetchSinglePagePerWebhook = scope === WEBHOOK_ACTIVITY_SCOPES.PREVIEW;
  const shouldFetchLastDayActivities = scope === WEBHOOK_ACTIVITY_SCOPES.PREVIEW;
  const webhookActivitiesSource = getWebhookActivitiesSource();
  const activitiesQueryTarget = useMemo(() => {
    if (webhookActivitiesSource === WEBHOOK_DATA_SOURCES.AGGREGATED) {
      return 'aggregated';
    }

    return webhooks.map((webhook) => webhook.id).sort().join(',');
  }, [webhookActivitiesSource, webhooks]);

  const {
    data: rawActivitiesResponse = usesPagination ? WEBHOOK_ACTIVITY_PAGINATION_PLACEHOLDER : [],
    isFetching: fetching,
    refetch: refetchActivities,
  } = useQuery({
    queryKey: [
      'webhook-activities',
      webhookActivitiesSource,
      scope,
      activitiesQueryTarget,
      ...(usesPagination ? [currentPage, rowsPerPage] : []),
    ],
    queryFn: () =>
      getWebhookActivities({
        ...(webhookActivitiesSource !== WEBHOOK_DATA_SOURCES.AGGREGATED && { apps: webhooks }),
        maxPagesPerApp: shouldFetchSinglePagePerWebhook ? 1 : Number.POSITIVE_INFINITY,        
        limit: usesPagination ? WEBHOOK_ACTIVITY_PAGE_SIZE : WEBHOOK_PREVIEW_LIMIT,
        page: currentPage,
        paginated: usesPagination,
      }),
    enabled:
      webhookActivitiesSource === WEBHOOK_DATA_SOURCES.AGGREGATED || webhooks.length > 0,
    placeholderData: usesPagination ? WEBHOOK_ACTIVITY_PAGINATION_PLACEHOLDER : [],
    keepPreviousData: usesPagination,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const rawActivities = useMemo(
    () => (usesPagination ? rawActivitiesResponse?.results || [] : rawActivitiesResponse),
    [rawActivitiesResponse, usesPagination]
  );
  const mappedActivities = useMemo(() => rawActivities.map(mapWebhookActivity), [rawActivities]);
  const eventOptions = useMemo(() => getWebhookEventOptions(webhooks), [webhooks]);

  const filteredActivities = useMemo(() => {
    const selectedStatuses = filter.status || [];
    const selectedPeriods = filter.period || [];
    const selectedEvents = filter.events || [];

    return mappedActivities.filter((activity) => {
      const matchesStatus =
        selectedStatuses.length === 0 || selectedStatuses.includes(activity.type);
      const matchesPeriod =
        selectedPeriods.length === 0 ||
        selectedPeriods.some((period) => isWebhookActivityInPeriod(activity, period));
      const matchesEvent =
        selectedEvents.length === 0 || selectedEvents.includes(activity.eventKey);

      return matchesStatus && matchesPeriod && matchesEvent;
    });
  }, [filter.events, filter.period, filter.status, mappedActivities]);

  useEffect(() => {
    if (rowsPerPage !== WEBHOOK_ACTIVITY_PAGE_SIZE) {
      setRowsPerPage(WEBHOOK_ACTIVITY_PAGE_SIZE);
    }
  }, [rowsPerPage, setRowsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [filter, setPage]);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const totalRows = usesPagination
    ? Number(rawActivitiesResponse?.count) || filteredActivities.length
    : filteredActivities.length;
  const hasNextPage = Boolean(rawActivitiesResponse?.next);
  const totalPagesByCount = Math.max(Math.ceil(totalRows / rowsPerPage), 1);
  const totalPages =
    usesPagination && hasNextPage
      ? Math.max(totalPagesByCount, currentPage + 1)
      : Math.max(totalPagesByCount, currentPage);

  useEffect(() => {
    if (usesPagination && !fetching && currentPage > totalPages) {
      setPage(totalPages);
    }
  }, [currentPage, fetching, setPage, totalPages, usesPagination]);

  const activities = useMemo(
    () =>
      usesPagination
        ? {
            count: totalRows,
            next: rawActivitiesResponse?.next || null,
            previous: rawActivitiesResponse?.previous || null,
            results: filteredActivities,
          }
        : {
            count: filteredActivities.length,
            results: filteredActivities.slice(startIndex, endIndex),
          },
    [
      endIndex,
      filteredActivities,
      rawActivitiesResponse,
      startIndex,
      totalRows,
      usesPagination,
    ]
  );

  const previewActivities = useMemo(
    () => filteredActivities.slice(0, WEBHOOK_PREVIEW_LIMIT),
    [filteredActivities]
  );

  return {
    table,
    activities,
    previewActivities,
    eventOptions,
    fetching,
    refetchActivities,
    usesPagination,
  };
}
